/**
 * Collection section — handles filter dropdowns with AJAX morphing
 * Uses the Section Rendering API + morph.js for seamless filter updates
 * without full-page reload.
 *
 * Architecture (Horizon-inspired):
 * - Desktop filters live in their own <form id="CollectionFiltersForm-desktop">
 * - Mobile drawer has its own <form data-mobile-filters-form>
 * - No duplicate inputs across viewports — FormData naturally scopes to each form.
 *
 * Provider-agnostic: a collection ordering provider (Searchspring, Algolia, etc.)
 * is imported directly and passed to `registerCollectionProvider()`. The provider
 * contract is { fetchProducts, reorderGrid, onRendered }. When no provider is
 * registered the collection renders with Shopify's native sort order.
 */

import { initAccordion } from '@stardust/accordion';
import { SCREENS } from '@stardust/constants';
import { sectionRenderer, morphSection, normalizeSectionId } from '@stardust/section-renderer';
import { observeProductCards } from '@stardust/swatch-hydrator';
import { initProductCards } from '@stardust/product-card';

// Provider contract — an object with:
//   fetchProducts()                       → Promise<data | null>
//   reorderGrid(data, gridElement)        → void   (mutates DOM order)
//   onRendered(data, gridElement)         → void   (set up tracking / observers)

let collectionProvider = null;

class CollectionSection {
  constructor(element) {
    this.element = element;
    this.debounceTimer = null;
    this._boundStuckScroll = null;
    // When ?q= is present and SS is active, the search page JS handles everything
    this.isSearchMode = new URLSearchParams(window.location.search).has('q');
    this.init();
  }

  /**
   * Derive Shopify section IDs from the DOM.
   * The .shopify-section wrapper divs have ids like "shopify-section-<key>".
   */
  getSectionId(el) {
    const wrapper = el?.closest('.shopify-section');
    if (!wrapper) return null;
    return normalizeSectionId(wrapper.id);
  }

  /**
   * The desktop filter form. Always re-query to survive morph cycles.
   */
  get desktopForm() {
    return document.getElementById('CollectionFiltersForm-desktop');
  }

  /**
   * The mobile drawer filter form. Always re-query to survive morph cycles.
   */
  get mobileForm() {
    return document.querySelector('[data-mobile-filters-form]');
  }

  init() {
    // Derive section IDs for morph targets
    this.collectionSectionId = this.getSectionId(
      document.querySelector('[data-section-type="collection"]')
    );
    this.filtersSectionId = this.getSectionId(
      document.getElementById('collection-filters-section')
    );

    // Hide apply buttons since JavaScript is loaded
    this.hideApplyButtons();
    this.setupStuckFilterBar();

    if (!this.desktopForm && !this.mobileForm && !this.isSearchMode) {
      console.warn('No filter forms found');
    }

    // ─── Event delegation (survives morph) ───────────────────────────

    // Intercept form submission from both desktop and mobile forms
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('#CollectionFiltersForm-desktop, .collection-filters-form, [data-mobile-filters-form]');
      if (form) {
        e.preventDefault();
        // Close the mobile drawer before morphing for clean UX
        if (form.hasAttribute('data-mobile-filters-form')) {
          document.dispatchEvent(new CustomEvent('mobile-filters:close'));
        }
        this.applyFiltersFromForm(form);
      }
    });

    // Auto-submit on checkbox/radio change inside the desktop form
    document.addEventListener('change', (e) => {
      // Match by ID (preferred) or by class (fallback for stale morph)
      const form = e.target.closest('#CollectionFiltersForm-desktop, .collection-filters-form');
      if (!form) return;

      if (e.target.matches('input[type="checkbox"], input[type="radio"]')) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.applyFiltersFromForm(form), 200);
      } else if (e.target.matches('input[type="number"]')) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.applyFiltersFromForm(form), 300);
      }
    });

    // Initialize exclusive accordion for mobile filter drawer
    const mobileFiltersForm = this.mobileForm;
    if (mobileFiltersForm) {
      initAccordion(mobileFiltersForm, { exclusive: true });
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      const dropdowns = document.querySelectorAll('[data-filter-dropdown]');
      dropdowns.forEach(dropdown => {
        if (!dropdown.contains(e.target)) {
          dropdown.removeAttribute('open');
        }
      });
    });

    // Intercept "remove filter" tag clicks and morph instead of navigating.
    // Only handles <a> tags (S&D server-rendered). SS-rendered <button> tags
    // handle their own click events via onFacetChange.
    document.addEventListener('click', (e) => {
      const removeLink = e.target.closest('a.collection-filters__control--remove-filter');
      if (!removeLink || !removeLink.href) return;

      e.preventDefault();
      const url = new URL(removeLink.href, window.location.origin);
      history.pushState({}, '', url);
      this.applyFiltersFromURL(url);
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.applyFiltersFromURL(new URL(window.location.href));
    });

    // Listen for SS filter changes (dual params in URL, trigger morph)
    document.addEventListener('ss:filters:applied', (e) => {
      const url = new URL(e.detail.url);
      this.applyFiltersFromURL(url);
    });
  }

  /**
   * Hide the no-JS fallback apply buttons.
   * Called on init and again after each morph (new buttons appear in morphed HTML).
   */
  hideApplyButtons() {
    document.querySelectorAll('.js-apply-button').forEach(button => {
      button.style.display = 'none';
    });
  }

  /**
   * Flag the mobile filter bar while it is stuck so CSS can style it full-bleed.
   * No CSS :stuck at our browser floor. Uses scroll over IntersectionObserver — IO
   * notifications arrive a frame behind and miss states entered without a threshold crossing.
   * Only writes on change; an unconditional write forces a style recalc on every read.
   */
  setupStuckFilterBar() {
    if (this._boundStuckScroll) {
      document.removeEventListener('scroll', this._boundStuckScroll);
      this._boundStuckScroll = null;
    }

    const bar = this.element.querySelector('[data-sticky-filter-bar]');
    const sentinel = this.element.querySelector('[data-sticky-filter-sentinel]');
    if (!bar || !sentinel) return;

    let queued = false;
    const syncStuck = () => {
      queued = false;
      // The bar is nav:hidden, so above that there is nothing to pin and nothing to measure.
      if (window.innerWidth >= SCREENS.nav) return;
      const stuck = String(sentinel.getBoundingClientRect().bottom <= 0);
      if (bar.dataset.stuck !== stuck) bar.dataset.stuck = stuck;
    };

    this._boundStuckScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(syncStuck);
    };

    document.addEventListener('scroll', this._boundStuckScroll, { passive: true });

    // Seed now: morph resets data-stuck, and the first scroll may be too late.
    syncStuck();
  }

  /**
   * Build filter URL from a form's current state and apply via morph.
   * Uses FormData to naturally scope to only the inputs within the given form —
   * no CSS-selector workarounds needed.
   *
   * @param {HTMLFormElement} form - The form to read state from
   */
  applyFiltersFromForm(form) {
    if (this.isSearchMode) return; // Search page JS handles filters
    if (!form) form = this.desktopForm;
    if (!form) return;

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }

    const url = new URL(form.action || window.location.pathname, window.location.origin);
    url.search = params.toString();

    // Update browser URL without reload
    history.pushState({}, '', url);

    this.applyFiltersFromURL(url);
  }

  /**
   * Fetch fresh section HTML from a URL and morph it into the current DOM.
   * When a collection provider is registered, fetches provider data in parallel
   * and reorders the product grid in the HTML fragment BEFORE morphing,
   * eliminating any visible reorder flicker.
   * @param {URL} url
   */
  async applyFiltersFromURL(url) {
    if (this.isSearchMode) return; // Search page JS handles filters

    // When the provider is in SS-only mode, it handles all rendering —
    // skip the morph cycle entirely so S&D doesn't interfere.
    if (collectionProvider?.isSSOnlyMode?.()) return;

    // Show a loading state on the grid
    const grid = this.element.querySelector('.collection-grid');
    if (grid) {
      grid.style.opacity = '0.5';
      grid.style.filter = 'blur(10px)';
      grid.style.pointerEvents = 'none';
      grid.style.transition = 'opacity 0.15s ease, filter 0.15s ease';
    }

    try {
      if (collectionProvider) {
        // --- Parallel fetch: sections HTML + provider data ---
        const [sectionsHTML, providerData] = await Promise.all([
          sectionRenderer.fetchSectionsHTML(
            [this.collectionSectionId, this.filtersSectionId],
            { url }
          ),
          collectionProvider.fetchProducts(),
        ]);

        // Morph the filters section first (no reordering needed)
        const filtersId = normalizeSectionId(this.filtersSectionId);
        const filtersHTML = sectionsHTML[filtersId];
        if (filtersHTML) {
          morphSection(this.filtersSectionId, filtersHTML);
        }

        // Reorder the collection section's grid in a temp fragment before morphing
        const collectionId = normalizeSectionId(this.collectionSectionId);
        let collectionHTML = sectionsHTML[collectionId];

        if (collectionHTML && providerData) {
          const fragment = new DOMParser().parseFromString(collectionHTML, 'text/html');
          const fragmentGrid = fragment.querySelector('.collection-grid');
          if (fragmentGrid) {
            collectionProvider.reorderGrid(providerData, fragmentGrid);
            // Serialize the modified fragment back to HTML
            const sectionEl = fragment.getElementById(`shopify-section-${collectionId}`);
            if (sectionEl) {
              collectionHTML = sectionEl.outerHTML;
            }
          }
        }

        // Morph the pre-sorted collection section into the live DOM
        if (collectionHTML) {
          morphSection(this.collectionSectionId, collectionHTML);
        }

        // After morph: check if S&D products match SS results.
        // Fall back to SS rendering when S&D returned 0 products, or when
        // the S&D products don't overlap with SS results (e.g., S&D filter
        // was ignored and returned the wrong product set).
        const liveGrid = this.element.querySelector('.collection-grid');
        if (liveGrid && providerData?.results?.length > 0) {
          const sdCards = liveGrid.querySelectorAll(':scope > [data-product-handle]');
          const sdProductCount = sdCards.length;
          let needsFallback = sdProductCount === 0;

          if (!needsFallback && sdProductCount > 0) {
            // Check handle overlap — S&D may have returned unfiltered products
            const ssHandles = new Set(providerData.results.map(r => r.handle));
            needsFallback = !Array.from(sdCards).some(
              card => ssHandles.has(card.dataset.productHandle),
            );
          }

          if (needsFallback && collectionProvider.renderFallback) {
            collectionProvider.renderFallback(providerData, liveGrid);
          } else {
            collectionProvider.onRendered(providerData, liveGrid);
          }
        } else if (providerData && liveGrid) {
          collectionProvider.onRendered(providerData, liveGrid);
        }
      } else {
        // --- No provider: simple single-request morph ---
        await sectionRenderer.renderSections(
          [this.collectionSectionId, this.filtersSectionId],
          { url, cache: false }
        );
      }

      // Re-hide apply buttons in the freshly morphed DOM
      this.hideApplyButtons();
      this.setupStuckFilterBar();

      // Re-hydrate swatches and variant switchers on the newly morphed product cards
      initProductCards();
      observeProductCards();
    } catch (error) {
      console.error('Failed to apply filters via section rendering:', error);
      // Fallback: perform a full-page navigation
      window.location.href = url.toString();
    } finally {
      // Clear loading state — re-query grid since morph may have replaced the element
      const updatedGrid = this.element.querySelector('.collection-grid');
      if (updatedGrid) {
        updatedGrid.style.opacity = '';
        updatedGrid.style.filter = '';
        updatedGrid.style.pointerEvents = '';
        updatedGrid.style.transition = '';
        // Morph may reintroduce searchspring-loading from the server-rendered HTML
        updatedGrid.classList.remove('searchspring-loading');
      }
    }
  }
}

// ─── Provider Registration ───────────────────────────────────────────────────

/**
 * Register a collection ordering provider. Validates the contract before storing.
 * @param {{init: Function, fetchProducts: Function, reorderGrid: Function, onRendered: Function}} provider
 * @returns {boolean} True when the provider satisfied the contract and was stored
 */
function registerCollectionProvider(provider) {
  const isValid = provider
    && typeof provider.init === 'function'
    && typeof provider.fetchProducts === 'function'
    && typeof provider.reorderGrid === 'function'
    && typeof provider.onRendered === 'function';

  if (!isValid) {
    console.warn('[collection] Provider rejected — missing init/fetchProducts/reorderGrid/onRendered:', provider);
    return false;
  }

  collectionProvider = provider;
  return true;
}

// ─── Initialization ──────────────────────────────────────────────────────────

// Searchspring is the only collection provider. theme.liquid server-renders the
// `ss-shop` body class whenever SS is enabled in theme settings on a collection page,
// so its presence is known at parse time — no runtime check needed. Kick off the import
// at module-eval rather than inside the DOMContentLoaded handler so the fetch overlaps
// the remaining deferred scripts instead of starting after them.
//
// A dynamic import is invisible to the preload scanner, so scripts.liquid emits a
// <link rel="modulepreload"> for searchspring-collection.js on SS collection pages —
// without it this request only starts once this module evaluates, which is later than
// the parser-discovered provider <script> tag it replaced.
const providerReady = document.body?.classList.contains('ss-shop')
  ? import('@stardust/searchspring-collection')
      .then((m) => m.provider)
      .catch((err) => {
        console.error('[collection] Searchspring provider import failed:', err);
        return null;
      })
  : null;

document.addEventListener('DOMContentLoaded', async () => {
  const collectionElement = document.querySelector('[data-section-type="collection"]');
  if (collectionElement) {
    new CollectionSection(collectionElement);
  }

  const provider = await providerReady;
  if (provider && registerCollectionProvider(provider)) {
    provider.init('.collection-grid');
  }
});
